import { ListGroupItem, Row, Col, FormCheck, Spinner, Badge } from "react-bootstrap";
import { FaSwimmingPool, FaCheck, FaQuestionCircle, FaCloudDownloadAlt, FaEye } from "react-icons/fa";
import { TbTruckDelivery, TbCoins } from "react-icons/tb";
import PrivateApi from "../../../services/PrivateApiService";
import { useState } from "react";
import { useAuthState } from "../../../context/AuthContext";
import { useQueryClient } from "react-query";

/**
 * PrivateDocDownloadListItem component displays a list item for downloading private documents related to invoices.
 * 
 * This component renders a list item that includes checkboxes for selecting documents to download,
 * icons representing the type of document, the title, date, and options to download the document.
 * It also provides functionality to handle the download of individual files and manage the download state.
 * It uses the PrivateApi service to fetch the document data and handle downloads.
 * It also allows users to ask questions about the document via a provided function.
 * 
 * @param {Object} invoice - The invoice object containing details about the document.
 * @param {boolean} downloaded - Indicates if the document has been downloaded.
 * @param {Function} question - Function to handle questions about the document.
 * @param {Function} downlaodListHandler - Function to handle the download list actions.
 * 
 * @returns ListGroupItem component displaying the document download options.
 */
const PrivateDocDownloadListItem = ({ invoice, downloaded, question, downlaodListHandler, preview }) => {

    const API = new PrivateApi(); // Initialize the API service
    const queryClient = useQueryClient(); // Get the query client for React Query
    const { user } = useAuthState(); // Get the authenticated user state
    const [downloadLoading, setDownloadLoading] = useState(false); // State to manage the loading state during download
    const [previewLoading, setPreviewLoading] = useState(false);
    const type = // Determine the type of document based on the invoice properties
        invoice.x_file_poolrechnung_name !== undefined ? "poolrechnung" :
        invoice.x_file_abrechnung_name !== undefined ? "abrechnung" :
        invoice.x_file_vorschreibung_name !== undefined ? "vorschreibung" :
        null;
    const color = // Set the color class based on the type of document
        type === "poolrechnung" ? "text-energo" :
        type === "abrechnung" ? "text-primary" :
        type === "vorschreibung" ? "text-warning" :
        null;
    const icon = // Determine the icon to display based on the type of document
        type === "poolrechnung" ? <FaSwimmingPool className={color} size={25} /> :
        type === "abrechnung" ? <TbTruckDelivery className={color} size={25} /> :
        type === "vorschreibung" ? <TbCoins className={color} size={25} /> :
        null;
    const title = // Set the title based on the type of document
        type === "poolrechnung" ? invoice.x_rechnungsart : 
        type === "abrechnung" ? "Energieabrechnung" : 
        type === "vorschreibung" ? "Vorschreibung" :
        null;
    const htmlColor = // Set the HTML color based on the type of document
        type === "poolrechnung" ? "#a2c13f" :
        type === "abrechnung" ? "#0d6efd" : 
        type === "vorschreibung"? "#ffc107": 
        "#000";
    const date =  // Format the date to German locale
        type === "poolrechnung" ? new Date(invoice.x_rechnungsdatum).toLocaleDateString('DE-de') :
        type === "abrechnung" ? new Date(invoice.x_eingangsdatum).toLocaleDateString('DE-de') :
        type === "vorschreibung" ? new Date(invoice.create_date).toLocaleDateString('DE-de') :
        "";
    const downloadedIcon = <FaCheck className="text-success" size={20} />; // Icon to indicate that the document has been downloaded


    // Handler for checkbox changes to manage the download list
    // It adds or removes the document from the download list based on the checkbox state
    const checkboxHandler = (e) => {    
        if (e.target.checked) {
            var opt = e.target.id.split(":")[2];
            downlaodListHandler(type, invoice.id, opt, "push");
        } else {
            downlaodListHandler(type, invoice.id, opt, "remove");
        }
    }

    // Function to download a single file based on the provided data
    // It fetches the file from the API, converts it from base64 to a Blob, and triggers a download
    // It also handles loading state and error handling during the download process
    // The data parameter is expected to be in the format "type:id:option" where option can be "file2", "mahnung", or "storno"
    // The function uses the API service to get the file data and then processes it for download
    // If the download is successful, it creates a Blob from the base64 string and triggers a download link
    // If the download fails, it sets the loading state to false and alerts the user
    const downloadSingleFile = (data) => {
        setDownloadLoading(true); // Set loading state to true while downloading

        // Call the API to get the single download data
        API.get_InvoiceSingleDownload(data)
            .then(response => { // Handle the response from the API
                setDownloadLoading(false); // Set loading state to false after the download is complete
                const file = response.data; // Extract the file data from the response
                if (response.status === 200) { // Check if the response status is 200 (OK)
                    const base64 = file.file.split(',').pop(); // Extract the base64 part of the file data
        
                    // construct a Blob from the base64 string
                    const binaryString = window.atob(base64);
                    const len = binaryString.length;
                    const bytes = new Uint8Array(len);

                    for (let i = 0; i < len; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }

                    // Create a blob from the bytes
                    const blob = new Blob([bytes], { type: "application/pdf" });

                    // Create a download link and trigger it
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = file.name;
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                    queryClient.invalidateQueries("GET_ALL_INVOICES_PRIVATE"); // Invalidate the query to refresh the invoice data
                }
            })
            .catch(error => { // Handle any errors that occur during the download
                setDownloadLoading(false); // Set loading state to false and log the error
                console.error("Download failed:", error);
                alert("Download fehlgeschlagen. Bitte versuchen Sie es später erneut.");
            }
        );
    }

    const previewDocument = (data) => {
        setPreviewLoading(true);
        API.get_InvoiceSinglePreview(data)
            .then(resp => {
                preview(resp.data.file);
                setPreviewLoading(false);
            })
            .catch(err => {
                setPreviewLoading(false);
            }
        );
    }

    // Render the ListGroupItem with the invoice details and download options
    return (
        <ListGroupItem>
            <Row>
                <Col xs="1" md="1" xl="1" className="text-start">
                    <FormCheck
                        type="checkbox"
                        id={`${type}:${invoice.id}`}
                        onChange={e => checkboxHandler(e)}
                        hidden={downloaded !== undefined }
                    />
                </Col>
                <Col xs="2" md="1" xl="1">{icon}</Col>
                <Col xs="9" md="4" xl="4">
                    {title}
                    {type === "abrechnung" && invoice.x_active === false &&
                        <Badge style={{marginLeft: 5}} pill bg="danger">storniert</Badge>
                    }
                </Col>
                <Col xs="6" md="2" xl="2">{date}</Col>
                <Col xs="6" md="2" xl="2" className="text-center">
                    <FaQuestionCircle 
                        hidden={type === "vorschreibung" && invoice.x_file_mahnung_name === "False"}
                        style={{cursor: "pointer"}}
                        className="text-muted mx-2" 
                        size={20} 
                        onClick={() => question(invoice)} 
                    />
                    {previewLoading ?
                        <Spinner animation="border" size="sm" className="mx-2" /> :
                        <FaEye 
                            hidden={type === "vorschreibung" && invoice.x_file_mahnung_name === "False"}
                            style={{cursor: "pointer"}} size={20} 
                            className="mx-2" 
                            onClick={() => previewDocument(`${type}:${invoice.id}`)} 
                        />
                    }
                    {downloadLoading ? 
                        <Spinner animation="border" size="sm" className="text-energo mx-2" /> :
                        <FaCloudDownloadAlt 
                            hidden={type === "vorschreibung" && invoice.x_file_mahnung_name === "False"}
                            style={{cursor: "pointer"}} 
                            className="text-energo mx-2" 
                            size={20} 
                            onClick={() => downloadSingleFile(`${type}:${invoice.id}`)} 
                        />
                    }
                </Col>
    
                <Col xs="12" md="2" xl="2" className="text-center">{invoice.x_datum_download !== "False" ? <FaCheck className="text-success" size={20} /> : null}</Col>
            </Row>
            {invoice.x_file_abrechnung2_name !== undefined && invoice.x_file_abrechnung2_name !== "False" &&
                <Row 
                    className="mt-1" 
                    style={invoice.x_datum_download !== "False" ? {background: "#A9A9A9", opacity: 0.6}: null}
                >
                    <Col xs="1" md="1" xl="1" className="text-start">
                        <FormCheck
                            type="checkbox"
                            id={`${type}:${invoice.id}:file2`}
                            onChange={e => checkboxHandler(e)}
                            hidden={downloaded !== undefined}
                        />
                    </Col>
                    <Col xs="2" md="1" xl="1">
                        <div style={{ height: 20, marginTop:-5, marginLeft:10, borderLeft: `1px solid ${htmlColor}`, borderBottom: `1px solid ${htmlColor}`}}></div>
                    </Col>
                    <Col xs="9" md="4" xl="4">Energieabrechnung Teil 2</Col>
                    <Col xs="6" md="2" xl="2"></Col>
                    <Col xs="6" md="2" xl="2" className="text-center">
                        <FaQuestionCircle style={{cursor: "pointer"}} className="text-muted mx-2" size={20} onClick={() => question(invoice, "Abrechnung2")} />
                        {previewLoading ?
                            <Spinner animation="border" size="sm" className="mx-2" /> :
                            <FaEye style={{cursor: "pointer"}} size={20} className="mx-2" onClick={() => previewDocument(`${type}:${invoice.id}:file2`)}/>
                        }    
                        {downloadLoading ? 
                            <Spinner animation="border" size="sm" className="text-energo mx-2" /> :
                            <FaCloudDownloadAlt style={{cursor: "pointer"}} className="text-energo mx-2" size={20} onClick={() => downloadSingleFile(`${type}:${invoice.id}:file2`)} />
                        }
                    </Col>
                    <Col xs="12" md="2" xl="2" className="text-center">
                        {invoice.x_datum_download !== "False" && downloadedIcon}
                    </Col>
                </Row>
            }
            {invoice.x_file_mahnung_name !== undefined && invoice.x_file_mahnung_name !== "False" &&
                <Row 
                    className="mt-1" 
                    style={invoice.x_datum_download_mahnung !== "False" ? {background: "#A9A9A9", opacity: 0.6}: null}
                >
                    <Col xs="1" md="1" xl="1" className="text-start">
                        <FormCheck
                            type="checkbox"
                            id={`${type}:${invoice.id}:mahnung`}
                            onChange={e => checkboxHandler(e)}
                            hidden={downloaded !== undefined}
                        />
                    </Col>
                    <Col xs="2" md="1" xl="1">
                        <div style={{ height: 20, marginTop:-5, marginLeft:10, borderLeft: `1px solid ${htmlColor}`, borderBottom: `1px solid ${htmlColor}`}}></div>
                    </Col>
                    <Col xs="9" md="4" xl="4">Mahnung</Col>
                    <Col xs="6" md="2" xl="2"></Col>
                    <Col xs="6" md="2" xl="2" className="text-center">
                        <FaQuestionCircle style={{cursor: "pointer"}} className="text-muted mx-2" size={20} onClick={() => question(invoice, "Mahnung")} />
                        {previewLoading ?
                            <Spinner animation="border" size="sm" className="mx-2" /> :
                            <FaEye style={{cursor: "pointer"}} size={20} className="mx-2" onClick={() => previewDocument(`${type}:${invoice.id}:mahnung`)}/>
                        }
                        {downloadLoading ? 
                            <Spinner animation="border" size="sm" className="text-energo mx-2" /> :
                            <FaCloudDownloadAlt style={{cursor: "pointer"}} className="text-energo mx-2" size={20} onClick={() => downloadSingleFile(`${type}:${invoice.id}:mahnung`)} />    
                        }
                    </Col>
                    <Col xs="12" md="2" xl="2" className="text-center">
                        {invoice.x_datum_download_mahnung !== "False" && downloadedIcon}
                    </Col>
                </Row>
            }
            {invoice.x_file_stornorechnung_name !== undefined && invoice.x_file_stornorechnung_name !== "False" &&
                <Row 
                    className="mt-1" 
                    style={invoice.x_datum_download_storno !== "False" ? {background: "#A9A9A9", opacity: 0.6}: null}
                >
                    <Col xs="1" md="1" xl="1" className="text-start">
                        <FormCheck
                            type="checkbox"
                            id={`${type}:${invoice.id}:storno`}
                            onChange={e => checkboxHandler(e)}
                            hidden={downloaded !== undefined}
                        />
                    </Col>
                    <Col xs="2" md="1" xl="1">
                        <div style={{ height: 20, marginTop:-5, marginLeft:10, borderLeft: `1px solid ${htmlColor}`, borderBottom: `1px solid ${htmlColor}`}}></div>
                    </Col>
                    <Col xs="9" md="4" xl="4">Stornorechnung</Col>
                    <Col xs="6" md="2" xl="2"></Col>
                    <Col xs="6" md="2" xl="2" className="text-center">
                        <FaQuestionCircle style={{cursor: "pointer"}} className="text-muted mx-2" size={20} onClick={() => question(invoice, "Stornorechnung")} />
                        {previewLoading ?
                            <Spinner animation="border" size="sm" className="mx-2" /> :
                            <FaEye style={{cursor: "pointer"}} size={20} className="mx-2" onClick={() => previewDocument(`${type}:${invoice.id}:storno`)}/>
                        }
                        {downloadLoading ? 
                            <Spinner animation="border" size="sm" className="text-energo mx-2" /> :
                            <FaCloudDownloadAlt style={{cursor: "pointer"}} className="text-energo mx-2" size={20} onClick={() => downloadSingleFile(`${type}:${invoice.id}:storno`)} />
                        }
                    </Col>
                    <Col xs="12" md="2" xl="2" className="text-center">
                        {invoice.x_datum_download_storno !== "False" && downloadedIcon}
                    </Col>
                </Row>
            }
        </ListGroupItem>
    );
}

export default PrivateDocDownloadListItem;